import { useMemo, useState } from 'react';
import { ContentCopy, Refresh } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Link as MuiLink,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';

import type {
  ConversionJob,
  RawConversionFailure,
  RawUploadGroup,
  ResourceImportSessionSummary,
} from '../store/hooks';

type ChipColor = 'default' | 'info' | 'success' | 'warning' | 'error';

/** A source retains its upload identity across separately identified outputs. */
interface MigrationUnit {
  id: string;
  label: string;
  fileCount?: number;
  sizeBytes?: number;
  rawPrefix?: string;
  inventoryStatus?: RawUploadGroup['inventory_status'];
  sourceState?: RawUploadGroup['source_state'];
  sessionId?: string;
  status?: string;
  stats?: Record<string, number>;
  createdAt?: string | null;
  conversion?: ConversionJob;
  /** Why the last conversion bootstrap rejected this source, when the server said. */
  conversionFailure?: RawConversionFailure | null;
}

interface MigrationImportsListProps {
  uploads: RawUploadGroup[];
  sessions: ResourceImportSessionSummary[];
  isLoading: boolean;
  hasLoadError?: boolean;
  onRefresh: () => void;
  onOpenBatch: (sessionId: string) => void;
  /** Add more files to a still-pre-conversion batch (raw upload id). */
  onAddFiles: (uploadId: string) => void;
  onDelete?: (uploadId: string) => Promise<void>;
  conversionJobs: ConversionJob[];
  conversionStatusKnown: boolean;
  conversionEnabled: boolean;
  onGo: (uploadId: string, retryOf?: string) => void;
  launchingSourceId: string | null;
  uploadsBusy: boolean;
}

function formatBytes(bytes?: number): string {
  if (!bytes) return '—';
  const units = ['B', 'KiB', 'MiB', 'GiB'];
  const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  return `${(bytes / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

/** Legacy sources can be linked by the trailing converted batch ID. */
function batchIdOf(s3Prefix?: string | null): string {
  if (!s3Prefix) return '';
  const parts = s3Prefix.split('/').filter(Boolean);
  return parts[parts.length - 1] ?? '';
}

/**
 * Operator-facing reason a conversion bootstrap was rejected (GDN-3859).
 *
 * The server sends one authored sentence carrying the limit, the actual value
 * and the allowed value, so that is what an operator reads. The derived
 * fallback covers a blank message: the structured fields are always present,
 * and a row that says only "Conversion failed" is the very thing this fixes.
 */
function conversionFailureReason(failure: RawConversionFailure): string | null {
  const authored = failure.message?.trim();
  if (authored) return authored;
  // Past here the server broke its own contract: `message` is required. Derive
  // a sentence only where one is defensible — a limits refusal, a limit this
  // code understands, and a ceiling to quote. Anything else keeps the chip and
  // says nothing, rather than inventing a reason or throwing mid-render.
  if (failure.code && failure.code !== 'raw_source_exceeds_limits') return null;
  if (failure.limit === 'bytes') {
    const max = failure.max_bytes;
    if (typeof max !== 'number' || max <= 0) return null;
    const observed = failure.total_size_bytes;
    // Only print the observed value when it supports the claim; an inventory
    // number that doesn't exceed the ceiling would make the sentence false.
    const actual =
      typeof observed === 'number' && observed > max ? formatBytes(observed) : 'its size';
    return `Source exceeds the size limit: ${actual} (limit ${formatBytes(max)}).`;
  }
  if (failure.limit !== 'files') return null;
  const max = failure.max_files;
  if (typeof max !== 'number' || max <= 0) return null;
  const observed = failure.file_count;
  // 'en-US' to match the server's own grouping, not the browser's locale.
  const actual =
    typeof observed === 'number' && observed > max
      ? `${observed.toLocaleString('en-US')} files`
      : 'its file count';
  return `Source exceeds the file limit: ${actual} (limit ${max.toLocaleString('en-US')}).`;
}

function isConfirmedEmpty(unit: MigrationUnit): boolean {
  return unit.inventoryStatus === 'ready' && unit.fileCount === 0;
}

/** Map the staging status onto the operator-facing lifecycle stage. */
function lifecycle(unit: MigrationUnit): { label: string; color: ChipColor } {
  if (unit.sourceState === 'deleting') return { label: 'Deleting…', color: 'info' };
  if (!unit.sessionId) {
    const job = unit.conversion;
    if (!job) {
      if (unit.inventoryStatus === 'refreshing') return { label: 'Counting files', color: 'info' };
      if (unit.inventoryStatus === 'failed')
        return { label: 'File counts unavailable', color: 'warning' };
      if (isConfirmedEmpty(unit)) return { label: 'No files found', color: 'default' };
      if (unit.fileCount === undefined || unit.fileCount === 0)
        return { label: 'Upload registered', color: 'default' };
      return { label: 'Uploaded', color: 'default' };
    }
    if (job.status === 'failed' || job.status === 'dead')
      return { label: job.published ? 'Needs attention' : 'Conversion failed', color: 'error' };
    if (job.status === 'pending') return { label: 'Queued', color: 'info' };
    if (job.published || job.status === 'completed') return { label: 'Staging', color: 'info' };
    if (job.phase === 'verifying') return { label: 'Verifying', color: 'info' };
    if (job.phase === 'publishing_verified_output')
      return { label: 'Uploading results', color: 'info' };
    return { label: 'Converting', color: 'info' };
  }
  switch (unit.status) {
    case 'ready_for_review':
      return { label: 'Ready to review', color: 'warning' };
    case 'importing':
      return { label: 'Committing', color: 'info' };
    case 'completed':
      return { label: 'Committed', color: 'success' };
    case 'failed':
      return { label: 'Failed', color: 'error' };
    case 'ingesting':
      return { label: 'Staging', color: 'info' };
    default:
      return { label: unit.status ?? 'Staged', color: 'info' };
  }
}

/**
 * One unified list of migration units. An "upload" and the "batch" it converts
 * into are two phases of the same source, so each row shows a
 * single source progressing Uploaded → Ready to review → Committed, rather than
 * exposing the raw-uploads and staged-batches tables separately.
 */
export function MigrationImportsList({
  uploads,
  sessions,
  isLoading,
  hasLoadError = false,
  onRefresh,
  onOpenBatch,
  onAddFiles,
  onDelete,
  conversionJobs,
  conversionStatusKnown,
  conversionEnabled,
  onGo,
  launchingSourceId,
  uploadsBusy,
}: MigrationImportsListProps) {
  const { enqueueSnackbar } = useSnackbar();
  const [deleteTarget, setDeleteTarget] = useState<MigrationUnit | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(false);

  const units = useMemo<MigrationUnit[]>(() => {
    const byId = new Map<string, MigrationUnit>();
    for (const u of uploads) {
      byId.set(u.upload_id, {
        id: u.upload_id,
        label: u.label || u.upload_id,
        fileCount: u.file_count ?? undefined,
        sizeBytes: u.total_size_bytes ?? undefined,
        rawPrefix: u.prefix,
        inventoryStatus: u.inventory_status,
        sourceState: u.source_state,
        createdAt: u.created_at,
        conversionFailure: u.conversion_failure,
      });
    }
    for (const job of conversionJobs) {
      const existing = byId.get(job.raw_batch_id);
      if (existing)
        byId.set(job.raw_batch_id, {
          ...existing,
          conversion: job,
          createdAt: existing.createdAt ?? job.created_at,
        });
    }
    // Keep the latest session if an older source has multiple staging attempts.
    const latestFirst = [...sessions].sort(
      (a, b) =>
        (b.created_at ?? '').localeCompare(a.created_at ?? '') ||
        b.import_session_id.localeCompare(a.import_session_id),
    );
    for (const s of latestFirst) {
      const outputId = batchIdOf(s.s3_prefix);
      const conversion = conversionJobs.find(
        job =>
          job.published &&
          job.verified &&
          (job.import_session_id === s.import_session_id || job.output_batch_id === outputId),
      );
      const id = s.raw_batch_id ?? conversion?.raw_batch_id ?? outputId;
      if (!id) continue;
      const existing = byId.get(id) ?? { id, label: id };
      if (existing.sessionId) continue;
      byId.set(id, {
        ...existing,
        sessionId: s.import_session_id,
        status: s.status,
        stats: s.import_stats ?? undefined,
        createdAt: existing.createdAt ?? s.created_at,
      });
    }
    // Newest first when we have a timestamp; otherwise stable by label.
    return [...byId.values()].sort((a, b) => {
      if (a.createdAt && b.createdAt) return b.createdAt.localeCompare(a.createdAt);
      if (a.createdAt) return -1;
      if (b.createdAt) return 1;
      return a.label.localeCompare(b.label);
    });
  }, [uploads, sessions, conversionJobs]);

  const currentDeleteTarget = units.find(unit => unit.id === deleteTarget?.id);
  const deleteEligible = Boolean(
    currentDeleteTarget &&
    conversionStatusKnown &&
    !currentDeleteTarget.conversion &&
    !currentDeleteTarget.sessionId &&
    (!currentDeleteTarget.sourceState || currentDeleteTarget.sourceState === 'available'),
  );

  const confirmDelete = async () => {
    if (
      !deleteTarget ||
      !onDelete ||
      !deleteEligible ||
      uploadsBusy ||
      launchingSourceId ||
      deleting
    )
      return;
    setDeleting(true);
    setDeleteError(false);
    try {
      await onDelete(deleteTarget.id);
      setDeleteTarget(null);
      enqueueSnackbar('Upload deletion requested', { variant: 'success' });
    } catch {
      setDeleteError(true);
    } finally {
      setDeleting(false);
    }
  };

  const copyPrefix = (prefix: string) => {
    void navigator.clipboard.writeText(prefix);
    enqueueSnackbar('Copied S3 prefix', { variant: 'success' });
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardHeader
        title="Imports"
        subheader="Upload files, press Go to convert and stage them, then review and commit the results."
        action={
          <Tooltip title="Refresh">
            <IconButton onClick={onRefresh} aria-label="Refresh imports">
              <Refresh />
            </IconButton>
          </Tooltip>
        }
      />
      <CardContent>
        {conversionStatusKnown && !conversionEnabled && uploads.length > 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Conversion is not available here yet. You can continue uploading files.
          </Alert>
        )}
        {!conversionStatusKnown && uploads.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Conversion status is unavailable. Refresh before starting a conversion or adding files.
          </Alert>
        )}
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : units.length === 0 ? (
          !hasLoadError && (
            <Typography variant="body2" color="text.secondary">
              No registered imports yet. Upload source files above, or refresh to find existing
              uploads.
            </Typography>
          )
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Source</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Files</TableCell>
                <TableCell align="right">Resources</TableCell>
                <TableCell align="right">Size</TableCell>
                <TableCell>Created</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {units.map(unit => {
                const stage =
                  !conversionStatusKnown && !unit.sessionId && !unit.conversion
                    ? { label: 'Status unavailable', color: 'default' as const }
                    : lifecycle(unit);
                const staged = unit.stats?.staged;
                const total = unit.stats?.total;
                const openable = Boolean(unit.sessionId);
                const job = unit.conversion;
                const retryable =
                  job && !job.published && (job.status === 'failed' || job.status === 'dead');
                const sourceAvailable = !unit.sourceState || unit.sourceState === 'available';
                // The recorded refusal describes the LAST bootstrap, and the
                // server clears it only once a later attempt is accepted — which
                // the uploads poll learns up to 30s late, while the chip flips
                // the instant Go is pressed. Show it only where it is still the
                // row's own story: no session, not launching, not being deleted,
                // and either no job at all (a bootstrap refused before one
                // existed) or a job that failed without publishing results.
                const showFailureReason =
                  Boolean(unit.conversionFailure) &&
                  !unit.sessionId &&
                  launchingSourceId !== unit.id &&
                  unit.sourceState !== 'deleting' &&
                  unit.sourceState !== 'deleted' &&
                  !job?.published &&
                  (!job || job.status === 'failed' || job.status === 'dead');
                const failureReason =
                  showFailureReason && unit.conversionFailure
                    ? conversionFailureReason(unit.conversionFailure)
                    : null;
                // Processing is reserved before dispatch, so an unaccepted launch may have
                // no job. Keep recovery available: the server reuses its stable request ID.
                const canStart =
                  unit.sourceState !== 'deleting' &&
                  unit.sourceState !== 'deleted' &&
                  (!job || retryable);
                const canDelete =
                  onDelete && sourceAvailable && !job && !unit.sessionId && conversionStatusKnown;
                return (
                  <TableRow key={unit.id} hover>
                    <TableCell>
                      {openable ? (
                        <MuiLink
                          component="button"
                          type="button"
                          disabled={
                            uploadsBusy || Boolean(launchingSourceId) || Boolean(deleteTarget)
                          }
                          onClick={() => onOpenBatch(unit.sessionId as string)}
                          sx={{ textAlign: 'left' }}
                        >
                          {unit.label}
                        </MuiLink>
                      ) : (
                        unit.label
                      )}
                    </TableCell>
                    <TableCell>
                      <Box role="status">
                        <Chip
                          size="small"
                          label={launchingSourceId === unit.id ? 'Starting…' : stage.label}
                          color={stage.color}
                        />
                        {failureReason && (
                          <Typography
                            variant="caption"
                            color="error"
                            sx={{ display: 'block', mt: 0.5 }}
                          >
                            {failureReason}
                          </Typography>
                        )}
                      </Box>
                      {job?.published &&
                        (job.status === 'failed' || job.status === 'dead') &&
                        !unit.sessionId && (
                          <Typography variant="caption" sx={{ display: 'block' }}>
                            Results uploaded; ingestion needs checking.
                          </Typography>
                        )}
                    </TableCell>
                    <TableCell align="right">{unit.fileCount ?? '—'}</TableCell>
                    <TableCell align="right">
                      {total != null ? (staged != null ? `${staged}/${total}` : total) : '—'}
                    </TableCell>
                    <TableCell align="right">{formatBytes(unit.sizeBytes)}</TableCell>
                    <TableCell>
                      {unit.createdAt ? new Date(unit.createdAt).toLocaleString() : '—'}
                    </TableCell>
                    <TableCell align="right">
                      {openable ? (
                        <MuiLink
                          component="button"
                          type="button"
                          disabled={
                            uploadsBusy || Boolean(launchingSourceId) || Boolean(deleteTarget)
                          }
                          onClick={() => onOpenBatch(unit.sessionId as string)}
                        >
                          {unit.status === 'completed'
                            ? 'View'
                            : unit.status === 'ready_for_review'
                              ? 'Review & commit'
                              : 'View progress'}
                        </MuiLink>
                      ) : unit.rawPrefix ? (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            gap: 1,
                          }}
                        >
                          {canStart && (
                            <Button
                              variant="contained"
                              size="small"
                              disabled={
                                isConfirmedEmpty(unit) ||
                                !conversionEnabled ||
                                !conversionStatusKnown ||
                                uploadsBusy ||
                                Boolean(launchingSourceId) ||
                                Boolean(deleteTarget)
                              }
                              onClick={() => onGo(unit.id, retryable ? job.job_id : undefined)}
                            >
                              {retryable ? 'Retry conversion' : 'Go'}
                            </Button>
                          )}
                          {!job && sourceAvailable && (
                            <MuiLink
                              component="button"
                              type="button"
                              disabled={
                                !conversionStatusKnown ||
                                uploadsBusy ||
                                Boolean(launchingSourceId) ||
                                Boolean(deleteTarget)
                              }
                              onClick={() => onAddFiles(unit.id)}
                              sx={{
                                '&:disabled': { color: 'text.disabled', textDecoration: 'none' },
                              }}
                            >
                              Add files
                            </MuiLink>
                          )}
                          {canDelete && (
                            <Button
                              size="small"
                              color="error"
                              disabled={
                                uploadsBusy || Boolean(launchingSourceId) || Boolean(deleteTarget)
                              }
                              aria-label={`Delete upload ${unit.label}`}
                              onClick={() => {
                                setDeleteError(false);
                                setDeleteTarget(unit);
                              }}
                            >
                              Delete
                            </Button>
                          )}
                          <Tooltip title="Copy source location">
                            <IconButton
                              size="small"
                              aria-label="Copy S3 prefix"
                              onClick={() => copyPrefix(unit.rawPrefix as string)}
                            >
                              <ContentCopy fontSize="inherit" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      ) : null}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>
      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => {
          if (!deleting) setDeleteTarget(null);
        }}
      >
        <DialogTitle>Delete upload?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete “{deleteTarget?.label}” and its source files? This cannot be undone. Only uploads
            with no conversion or ingestion history can be deleted.
          </DialogContentText>
          {deleteError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              Could not delete this upload. It may have started processing, or deletion is
              unavailable. Refresh the imports list before trying again.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button disabled={deleting} onClick={() => setDeleteTarget(null)}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={!deleteEligible || deleting || uploadsBusy || Boolean(launchingSourceId)}
            onClick={() => void confirmDelete()}
          >
            {deleting ? 'Deleting…' : 'Delete upload'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
